import type { CSSProperties, ReactNode } from 'react'

// ============================================
// Keyframes for bouncing dots animation
// ============================================

const keyframes = `
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
`

// ============================================
// Styles
// ============================================

const containerStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: '#f5f0e6',
  gap: '16px',
}

const brandNameStyles: CSSProperties = {
  fontFamily: "'Fraunces', serif",
  fontSize: '2rem',
  fontWeight: 700,
  color: '#1a1510',
  margin: 0,
}

const loadingTextStyles: CSSProperties = {
  fontFamily: "'Caveat', cursive",
  fontSize: '1.4rem',
  fontWeight: 600,
  color: '#7a6f63',
  display: 'flex',
  alignItems: 'baseline',
}

const dotStyles: CSSProperties = {
  display: 'inline-block',
}

// ============================================
// Component
// ============================================

export interface AppLoaderProps {
  /** Optional message to display below the loading indicator */
  message?: string
}

/**
 * Branded loading/splash screen component.
 * Shows while the app is initializing (auth, lazy loading, etc).
 * Uses notebook aesthetic with paper texture and hand-drawn feel.
 */
export function AppLoader({ message }: AppLoaderProps): ReactNode {
  const messageStyles: CSSProperties = {
    fontFamily: "'Nunito', sans-serif",
    fontSize: '0.875rem',
    color: '#7a6f63',
    margin: 0,
    marginTop: '8px',
  }

  return (
    <>
      <style>{keyframes}</style>
      {/* Paper texture overlay */}
      <svg
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1000,
          opacity: 0.35,
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="paper-loader" x="0%" y="0%" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="0.04" numOctaves="5" result="noise" />
            <feDiffuseLighting in="noise" lightingColor="#f5f0e6" surfaceScale="2" result="lit">
              <feDistantLight azimuth="45" elevation="60" />
            </feDiffuseLighting>
          </filter>
        </defs>
        <rect width="100%" height="100%" filter="url(#paper-loader)" />
      </svg>

      <div style={containerStyles} role="status" aria-label="Loading application">
        <h1 style={brandNameStyles}>Mellia</h1>
        <div style={loadingTextStyles}>
          <span>loading</span>
          <span style={{ ...dotStyles, animation: 'bounce 0.4s ease-in-out infinite' }}>.</span>
          <span style={{ ...dotStyles, animation: 'bounce 0.4s ease-in-out 0.1s infinite' }}>
            .
          </span>
          <span style={{ ...dotStyles, animation: 'bounce 0.4s ease-in-out 0.2s infinite' }}>
            .
          </span>
        </div>
        {message && <p style={messageStyles}>{message}</p>}
      </div>
    </>
  )
}
